import { marketLocationMasterDataDevices } from '@e2m-customer-portal/core';
import { useMarketLocation } from '@e2m-customer-portal/hooks';
import { convertMeasure } from '@e2m-customer-portal/shared';
import { Container, ErrorComponent, MasterDataItem, PageTitle } from '@e2m-customer-portal/ui';
import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { makeStyles } from 'tss-react/mui-compat';

const useStyles = makeStyles()((theme) => ({
  container: {
    margin: theme.spacing(0, 3),
    '&:first-of-type': {
      marginTop: theme.spacing(3),
    },
  },
}));

interface MasterDataMachinesProps {
  marketLocationId: string;
}

export function MasterDataMachines({
  marketLocationId,
}: Readonly<MasterDataMachinesProps>): ReactElement<MasterDataMachinesProps> {
  const { classes } = useStyles();
  const [marketLocation, error] = useMarketLocation(marketLocationId);
  const [t] = useTranslation();

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hash]);

  if (error) {
    return <ErrorComponent error={error} />;
  }

  return (
    <>
      <PageTitle title={t('MASTER_DATA_HEADER')} />
      {marketLocation?.linkedMarketLocation && (
        <div className="font-semibold text-blue-grey-200 px-6 pt-6">{t(`SHARED_${marketLocation.type}`)}: </div>
      )}
      {marketLocation?.machines.map((machine) => (
        <Container
          key={machine.id}
          id={machine.id}
          title={machine.name}
          maxWidth="md"
          className={classes.container}
          collapsible
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
            <MasterDataItem header={t('MASTER_DATA_GRID_ALIAS_NAME')} body={machine.name} />
            <MasterDataItem
              header={t('MASTER_DATA_GRID_TECHNOLOGY')}
              body={t(`SHARED_TECHNOLOGY_${machine.machineTechnology}`)}
            />
            <MasterDataItem
              header={t('MASTER_DATA_GRID_TU_INSTALLED_CAPACITY')}
              body={convertMeasure(machine.installedPower, 'MW')}
            />
            <MasterDataItem header={t('MASTER_DATA_GRID_TUS')}>
              <ol className="list-none m-0 p-0">
                {machine.devices.map((device) => (
                  <li key={device.id}>
                    <Link to={`${marketLocationMasterDataDevices.getLink(marketLocation.id)}#${device.id}`}>
                      {device.externalName ? `${device.externalName} (${device.name})` : device.name}
                    </Link>
                  </li>
                ))}
              </ol>
            </MasterDataItem>
          </div>
        </Container>
      ))}

      {marketLocation?.linkedMarketLocation && (
        <>
          <div className="font-semibold text-blue-grey-200 px-6 pt-8 pb-6">
            {t(`SHARED_${marketLocation.linkedMarketLocation.type}`)}:
          </div>
          {marketLocation.linkedMarketLocation.machines.map((machine) => (
            <Container
              key={machine.id}
              id={machine.id}
              title={machine.name}
              maxWidth="md"
              className={classes.container}
              collapsible
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
                <MasterDataItem header={t('MASTER_DATA_GRID_ALIAS_NAME')} body={machine.name} />
                <MasterDataItem
                  header={t('MASTER_DATA_GRID_TECHNOLOGY')}
                  body={t(`SHARED_TECHNOLOGY_${machine.machineTechnology}`)}
                />
                <MasterDataItem
                  header={t('MASTER_DATA_GRID_TU_INSTALLED_CAPACITY')}
                  body={convertMeasure(machine.installedPower, 'MW')}
                />
                <MasterDataItem header={t('MASTER_DATA_GRID_TUS')}>
                  <ol className="list-none m-0 p-0">
                    {machine.devices.map((device) => (
                      <li key={device.id}>
                        <Link to={`${marketLocationMasterDataDevices.getLink(marketLocation.id)}#${device.id}`}>
                          {device.externalName ? `${device.externalName} (${device.name})` : device.name}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </MasterDataItem>
              </div>
            </Container>
          ))}
        </>
      )}
    </>
  );
}
